/**
 * The content this app ships with, in one place: the app reads it here, and so does the
 * production gate (scripts/verify-content-validation.mjs), which bundles this very file. Whatever
 * is wired in below is what the gate checks — there is no second copy of the answer for the two
 * to disagree about.
 *
 * One fiqh core, validated once by a scholar, for every owner wherever they live; and a civil
 * module per country, validated by I-DINA on its official texts. A country is offered as « règles
 * intégrées » only when its module is listed in INTEGRATED_COUNTRIES; anywhere else, the owner
 * gets the core and a banner saying the civil part is not covered.
 */
import type { CountryPackMeta, ValidationRole } from '../domain';
import {
  BE_CIVIL_META,
  BE_CIVIL_REQUIRED_VALIDATIONS,
} from '../domain/testing/be-civil-module.header';
import { FIQH_CORE_META, FIQH_CORE_REQUIRED_VALIDATIONS } from '../domain/testing/fiqh-core.header';
import {
  FR_CIVIL_META,
  FR_CIVIL_REQUIRED_VALIDATIONS,
} from '../domain/testing/fr-civil-module.header';

export {
  FIQH_CORE_META as CONTENT_CORE_META,
  FIQH_CORE_REQUIRED_VALIDATIONS as CONTENT_CORE_REQUIRED_VALIDATIONS,
};

/** The light part of each country module the app offers: what the gate and the first screen read. */
export interface CivilModuleHeader {
  readonly meta: CountryPackMeta;
  readonly requiredValidations: readonly ValidationRole[];
}

/**
 * The countries whose civil rules are built in: Belgium and France, each module validated by
 * I-DINA on the official texts. A country joins this list once its module is.
 */
export const CIVIL_MODULE_HEADERS: Readonly<Record<string, CivilModuleHeader>> = Object.freeze({
  BE: Object.freeze({
    meta: BE_CIVIL_META,
    requiredValidations: BE_CIVIL_REQUIRED_VALIDATIONS,
  }),
  FR: Object.freeze({
    meta: FR_CIVIL_META,
    requiredValidations: FR_CIVIL_REQUIRED_VALIDATIONS,
  }),
});

export const INTEGRATED_COUNTRIES: readonly string[] = Object.freeze(
  Object.keys(CIVIL_MODULE_HEADERS),
);

/** What is fetched the first time a part is resolved: see content-pack.lazy.ts. */
export const loadContentLazily = () => import('./content-pack.lazy');

/** The sections and their rules: the heavy part, fetched the first time a part is resolved. */
export const loadContentPack = () =>
  loadContentLazily().then((lazy) =>
    Object.freeze({
      core: lazy.PROVISIONAL_FIQH_CORE,
      modules: lazy.CIVIL_MODULES,
    }),
  );
